import { Agenda, Dropdown, Input, Option, Popup, TopBar } from '@sam/components';
import { AgendaEntryGroup } from '@sam/components/src/Agenda/Agenda.types';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  EmploymentType,
  Language,
  Right,
  SchambeckUser,
  Schedule,
  ScheduleType,
  createNewAbsenceSchedule,
  deleteAbsenceSchedule,
  generateNotification,
  updateAbsenceSchedule,
  useData,
} from 'shared';
import { NotificationType } from 'shared/src/notification/notification.types';
import { getSchedulesForMultipleUsers } from 'shared/src/project/Project.axios';
import { ScheduleEdit } from '../../components/schedule/ScheduleEdit';
import { useUser } from '../../components/UserContext';
import {
  generateEmptyAppointmentDetails,
  generateEmptySchedule,
} from '../../utils/project/Project.utils';
import { generatePlanningBoardEntries, isUserAllowedToDo } from '../../utils/user/User.utils';

export const PlanningBoard: React.FC = () => {
  const { axios, user } = useUser();
  const { data: loadedUsers, mutate: refreshUsers } = useData('SCHAMBECK_USERS_ALL_ACTIVE', {
    config: { fallbackData: [] },
  });

  const { data: loadedHolidays } = useData('HOLIDAYS_ALL', {
    config: { fallbackData: [] },
  });
  const { data: loadedAbsenceReasons } = useData('ABSENCE_REASONS_ALL_ACTIVE', {
    config: { fallbackData: [] },
  });
  const { data: loadedProjects } = useData('PROJECT_ALL_ACTIVE', {
    config: { fallbackData: [] },
  });

  const { data: loadedUserFunctions } = useData('USER_FUNCTION_ALL_ACTIVE', {
    config: { fallbackData: [] },
  });

  const [entryGroups, setEntryGroups] = useState<AgendaEntryGroup[]>([]);
  const [scheduleToEdit, setScheduleToEdit] = useState<Schedule>();
  const [userSchedules, setUserSchedules] = useState<Schedule[]>([]);
  const [filterValue, setFilterValue] = useState<string>();
  const [selectedUserFunctions, setSelectedUserFunctions] = useState<string[]>([]);
  const [selectedEmploymentTypes, setSelectedEmploymentTypes] = useState<EmploymentType[]>([]);

  const { t } = useTranslation();

  // Hook to load schedules for users to display
  useEffect(() => {
    if (loadedUsers.length <= 0) return;
    getSchedulesForMultipleUsers(
      axios,
      loadedUsers.map((user) => user.id),
    ).then(setUserSchedules);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadedUsers]);

  //Hook to generate the agenda entries after all neccessary data is available
  useEffect(() => {
    const filteredUsers: SchambeckUser[] = loadedUsers.filter((user) => {
      const functionIncluded: boolean =
        selectedUserFunctions.length === 0 || selectedUserFunctions.includes(user.userFunctionId);
      const typeIncluded: boolean =
        selectedEmploymentTypes.length === 0 ||
        selectedEmploymentTypes.includes(user.contractInformation.employmentType);
      return functionIncluded && typeIncluded;
    });
    setEntryGroups(
      generatePlanningBoardEntries(
        userSchedules,
        filteredUsers,
        loadedProjects,
        loadedAbsenceReasons,
      ),
    );
  }, [
    loadedUsers,
    selectedEmploymentTypes,
    selectedUserFunctions,
    loadedHolidays,
    userSchedules,
    user.id,
    loadedAbsenceReasons,
    loadedProjects,
    filterValue,
  ]);

  /**
   * Submit handler to create a new Absence
   * @param Schedule to save
   */
  const handleSubmit = async (schedule: Schedule): Promise<void> => {
    if (!scheduleToEdit) return Promise.resolve();
    if (!scheduleToEdit.appointmentDetail?.reasonId) {
      generateNotification({
        type: NotificationType.WARNING,
        value: t('general.notification.error.absenceReasonMissing'),
      });
      return;
    }
    if (!scheduleToEdit.id)
      return createNewAbsenceSchedule(axios, schedule).then((createdSchedule) => {
        if (createdSchedule) {
          generateNotification({
            type: NotificationType.SUCCESS,
            value: t('general.notification.success.absenceCreate'),
          });
          setScheduleToEdit(undefined);
          refreshUsers();
          setUserSchedules((prev) =>
            prev.map((schedule) =>
              schedule.id === createdSchedule.id ? createdSchedule : schedule,
            ),
          );
        }
      });
    else
      return updateAbsenceSchedule(axios, schedule).then((updatedSchedule) => {
        if (updatedSchedule) {
          generateNotification({
            type: NotificationType.SUCCESS,
            value: t('general.notification.success.absenceUpdate'),
          });
          setScheduleToEdit(undefined);
          refreshUsers();
          setUserSchedules((prev) =>
            prev.map((schedule) =>
              schedule.id === updatedSchedule.id ? updatedSchedule : schedule,
            ),
          );
        }
      });
  };

  /**
   *Helper method to check if a selected item is a user schedule and update it
   * @param id of the entry to edit. Consisting of the userid + scheduleId
   */
  const handleEdit = (id: string): void => {
    const schedule: Schedule | undefined = userSchedules.find((schedule) =>
      id.includes(schedule.id),
    );
    if (schedule?.type === ScheduleType.USER_ABSENCE) setScheduleToEdit(schedule);
    else {
      setScheduleToEdit(undefined);
      generateNotification({
        type: NotificationType.INFO,
        value: t('general.notification.error.selectedProjectPlanning'),
      });
    }
  };

  /**
   * Util method to delete an entry from the agenda
   * @param id of the entry to edit. Consisting of the userid + scheduleId
   */
  const handleDelete = (id: string): void => {
    const schedule: Schedule | undefined = userSchedules.find((schedule) =>
      id.includes(schedule.id),
    );
    if (schedule?.type === ScheduleType.USER_ABSENCE)
      deleteAbsenceSchedule(axios, schedule.id).then((success) => {
        if (success) {
          generateNotification({
            type: NotificationType.SUCCESS,
            value: 'Erfolgreich gelöscht',
          });
          refreshUsers();
        }
        setUserSchedules((prev) => prev.filter((schedule) => schedule.id !== id));
        setScheduleToEdit(undefined);
      });
    else {
      setScheduleToEdit(undefined);
      generateNotification({
        type: NotificationType.INFO,
        value: t('general.notification.error.selectedProjectPlanning'),
      });
    }
  };

  /**
   * Holds the options for userFunction without executive functions
   */
  const functionOptions: Option[] = useMemo((): Option[] => {
    return loadedUserFunctions
      .filter((userFunction) => !userFunction.executiveUserId)
      .map((userFunction) => ({
        label: userFunction.names.get(user.language || Language.DE),
        value: userFunction.id,
      }));
  }, [loadedUserFunctions]);

  return (
    <form>
      {scheduleToEdit && loadedUsers && isUserAllowedToDo(user.right, Right.ABSENCE_CREATE) && (
        <Popup
          isOpen={!!scheduleToEdit}
          onClose={() => setScheduleToEdit(undefined)}
          children={
            <ScheduleEdit
              handleScheduleCreate={handleSubmit}
              handleScheduleDelete={handleDelete}
              schedule={scheduleToEdit}
              updateSchedule={setScheduleToEdit}
            />
          }
        />
      )}
      <TopBar title={t('pages.planningBoard.topBarHeadline')}>
        <Input type="text" value={filterValue} onChange={setFilterValue} maxWidth={200} />
      </TopBar>
      <div className="planning-board__wrapper">
        <div className="three-columns">
          <Dropdown
            options={Object.values(EmploymentType).map((type) => ({
              label: t(`general.employmentType.${type}`),
              value: type,
            }))}
            onChangeMultiple={(types: string[]) =>
              setSelectedEmploymentTypes(types as EmploymentType[])
            }
            label={t('pages.planningBoard.employmentType')}
            multi
            selectedMultiOptions={selectedEmploymentTypes}
          />
          <Dropdown
            options={functionOptions}
            selectedMultiOptions={selectedUserFunctions}
            multi
            onChangeMultiple={(selectedFunctions) => setSelectedUserFunctions(selectedFunctions)}
            label={t('pages.planningBoard.userFunction')}
          />
        </div>
        <Agenda
          filterValue={filterValue}
          deleteItem={handleDelete}
          handleEdit={handleEdit}
          entryGroups={entryGroups}
          addItem={(item) => {
            setScheduleToEdit(
              generateEmptySchedule({
                startDate: new Date(item.start || new Date()),
                endDate: new Date(item.start || new Date()),
                referenceId: item.group?.toString() || '',
                appointmentDetail: {
                  ...generateEmptyAppointmentDetails(),
                  reasonId: '',
                },
                type: ScheduleType.USER_ABSENCE,
              }),
            );
          }}
        />
      </div>
    </form>
  );
};
